import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress, // Add loader
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { modalStyle, headerStyle, bottomCloseButtonStyle } from '../OrderEntry/styles';
import { tokens } from "../../Common/designTokens"; // added
import { formatPoolFactor } from "../../Utils/poolFactorFormatter";
import MarketDataInfoPanel from "../OrderEntry/MarketDataInfoPanel";

const btypes = {
  Gov: {
    fields: [
      "ratingSnp",
      "coupon",
      "maturity",
      // "duration",
      "zeroCoupon",
      "callable",
    ],
  },
  Muni: {
    fields: [
      "ratingSnp",
      "coupon",
      "maturity",
      // "duration",
      "zeroCoupon",
      "callable",
      "state",
    ],
  },
  Corp: {
    fields: [
      "ratingSnp",
      "couponRange",
      "maturity",
      // "duration",
      "zeroCoupon",
      "callable",
      "sector",
    ],
  },
};

/**
 * @param showMarketData   Opt-in. Off by default, so the six screens that already open this
 *                         dialog (Inventory, Account Positions, Bond Ladder, Order Preview,
 *                         Portfolio Rebalance, Account Position Explorer) are untouched and show
 *                         exactly what they showed before. Only the List RFQ tab's identifier
 *                         icon turns it on, because that is the one place a trader asked to see
 *                         the book alongside the reference data.
 * @param marketData       Best bid/offer, already normalised by Utils/marketDataDetail.
 * @param marketDataDepth  Depth rows, same source.
 * @param marketDataError  Message to show instead of the table - "not available for this
 *                         security" is a normal answer here, not a failure of the dialog.
 * @param marketDataLoading Whether the quote fetch is still in flight. Tracked separately from
 *                         `loading`: the two calls are independent, so the reference data can
 *                         paint as soon as it lands rather than waiting on the book.
 */
const BondDetailsModal = ({
  open,
  onClose,
  bondDetails,
  loading,
  showMarketData = false,
  marketData = null,
  marketDataDepth = [],
  marketDataError = null,
  marketDataLoading = false,
}) => {

  // Helper: consider only null/undefined as "missing"
  const isDefined = (v) => v !== null && v !== undefined;

  // Parse numbers but preserve empty string as an explicit empty value (not missing)
  const parseNumberOrUndefined = (v) => {
    if (!isDefined(v) || v === "") return v === "" ? "" : undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  // Helper for number formatting — show '---' only for null/undefined, keep "" as ""
  const formatNumber = (num) => {
    if (!isDefined(num)) return "---";
    if (num === "") return "";
    const n = Number(num);
    return Number.isFinite(n) ? n.toLocaleString() : "---";
  };

  // Helper for price formatting — show '---' only for null/undefined, keep "" as ""
  const formatPrice = (num) => {
    if (!isDefined(num)) return "---";
    if (num === "") return "";
    const n = Number(num);
    return Number.isFinite(n)
      ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "---";
  };

  // Helper for date formatting (dd/mm/yyyy) — '---' only for null/undefined, empty string stays ""
  const formatDate = (dateStr) => {
    if (!isDefined(dateStr)) return "---";
    if (dateStr === "") return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "---";
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // New helper: format maturity to MM/DD/YYYY
  const formatDateToMMDDYYYY = (dateStr) => {
    if (!isDefined(dateStr)) return "---";
    if (dateStr === "") return "";
    try {
      let valueStr = dateStr.toString();
      if (!valueStr.endsWith('Z')) valueStr += 'Z';
      const date = new Date(valueStr);
      if (isNaN(date.getTime())) return "---";
      const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(date.getUTCDate()).padStart(2, '0');
      const yyyy = date.getUTCFullYear();
      return `${mm}/${dd}/${yyyy}`;
    } catch (e) {
      console.error('Error formatting date to MM/DD/YYYY:', e, 'for value:', dateStr);
      return '---';
    }
  };

  const getNormalizedBondType = (type) => {
    if (!type) return null;
    const normalized = type.toLowerCase();
    if (normalized === 'corp') return 'Corp';
    if (normalized === 'gov') return 'Gov';
    if (normalized === 'muni') return 'Muni';
    return null;
  };

  const getRelevantFields = () => {
    if (!bondDetails) return [];

    const securityCharge = isDefined(bondDetails.securityCharge)
      ? bondDetails.securityCharge
      : bondDetails.secMasterDTO?.securityCharge;
    const poolFactor = isDefined(bondDetails.poolFactor)
      ? bondDetails.poolFactor
      : bondDetails.secMasterDTO?.poolFactor;

    // Build allFields using helpers that show '---' only for null/undefined
    const allFields = [
      { label: "CUSIP", value: isDefined(bondDetails.cusip) ? bondDetails.cusip : "---" },
      { label: "Bond Type", value: isDefined(bondDetails.bondType) ? bondDetails.bondType : "---" },
      { label: "Convention Method", value: isDefined(bondDetails.conventionMethod) ? bondDetails.conventionMethod : "---" },
      { label: "Short Description", value: isDefined(bondDetails.description) ? bondDetails.description : (isDefined(bondDetails.securityDesc) ? bondDetails.securityDesc : "---") },
      { label: "Income Source", value: isDefined(bondDetails.incomeSource) ? bondDetails.incomeSource : "---" },
      { label: "Factored Securities", value: isDefined(securityCharge) ? securityCharge : "---" },
      ...(isDefined(securityCharge) ? [{ label: "Factor", value: formatPoolFactor(poolFactor) }] : []),
      { label: "Sub Type", value: isDefined(bondDetails.bondSubType) ? bondDetails.bondSubType : (isDefined(bondDetails.subType) ? bondDetails.subType : "---") },
      { label: "Issue Date", value: formatDate(bondDetails.issueDate) },
      { label: "Coupon Type", value: isDefined(bondDetails.couponType) ? bondDetails.couponType : "---" },
      { label: "Interest Payment Frequency", value: isDefined(bondDetails.interestPaymentFrequency) ? bondDetails.interestPaymentFrequency : "---" },
      { label: "First Coupon Date", value: formatDate(bondDetails.firstCouponDate) },
      { label: "Last Coupon Date", value: formatDate(bondDetails.lastCouponDate) },
    ];

    const rawBondType = bondDetails.bondType;
    const bondType = getNormalizedBondType(rawBondType);
    
    if (!bondType || !btypes[bondType]) {
      return allFields;
    }

    const typeSpecificFields = btypes[bondType].fields.map(field => {
      switch (field) {
        case "ratingSnp":
          return { 
            label: "Ratings - S&P/Moody's", 
            value:
              (isDefined(bondDetails.ratingSnp) ? bondDetails.ratingSnp : "---") +
              "/"+
              (isDefined(bondDetails.ratingMoody) ? bondDetails.ratingMoody : "---")
          };
        case "ratingMoody":
          return null;
        case "coupon":
        case "couponRange":
          return { label: "Coupon", value: isDefined(bondDetails.coupon) ? bondDetails.coupon : "---" };
        case "maturity":
          // Use MM/DD/YYYY for maturity
          return { label: "Maturity", value: formatDateToMMDDYYYY(bondDetails.maturity) };
        // case "duration":
        //   return { label: "Duration", value: bondDetails.duration || "---" };
        case "zeroCoupon":
          return { label: "Zero Coupon", value: isDefined(bondDetails.zeroCoupon) ? bondDetails.zeroCoupon : "---" };
        case "callable":
          return { label: "Callable", value: isDefined(bondDetails.callable) ? bondDetails.callable : "---" };
        case "state":
          return { label: "Gov State", value: isDefined(bondDetails.state) ? bondDetails.state : "---" };
        case "sector":
          return { label: "Sector", value: isDefined(bondDetails.sector) ? bondDetails.sector : "---" };
        default:
          return null;
      }
    }).filter(field => field !== null);

    // Combine and deduplicate by label
    const combinedFields = [...allFields, ...typeSpecificFields];
    const seenLabels = new Set();
    const dedupedFields = combinedFields.filter(field => {
      if (seenLabels.has(field.label)) return false;
      seenLabels.add(field.label);
      return true;
    });

    return dedupedFields;
  };

  const displayFields = getRelevantFields();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          // Merge existing modalStyle but ensure light background and 8px radius
          ...modalStyle,
          width: '800px',
          bgcolor: tokens.colors.bodyBackgroundColor,
          border: `1px solid ${tokens.colors.bodyBorderColor50}`,
          borderRadius: "8px",
        }
      }}
    >
      <DialogTitle sx={headerStyle}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
          Bond Details
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ p: 0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
            <CircularProgress sx={{ color: tokens.palette.primary }} />
          </Box>
        ) : bondDetails && bondDetails.error ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
            <Typography color={tokens.colors.fontColour90} variant="body1">
              {bondDetails.message || "No details found for bond."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {displayFields.map((field, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor: tokens.colors.defaultGridRowColor, // light panel bg
                  borderRadius: "8px",                         // consistent 8px radius
                  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                  px: 1,
                  py: 0.5,
                  minWidth: "200px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: tokens.colors.fontColour85, mb: 0.1 }} // lighter caption
                >
                  {field.label}
                </Typography>
                <Typography variant="body2" sx={{ color: tokens.colors.fontColour }}>
                  {field.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {/* Below the reference data, not above it: the dialog is opened to identify a bond, and
            the book is context for that. MarketDataInfoPanel is the same component the single
            ticket renders under its form, fed from the same normaliser, so the table reads
            identically on both screens. */}
        {showMarketData && (
          <MarketDataInfoPanel
            marketData={marketData}
            depthData={marketDataDepth}
            errorMessage={marketDataError}
            isLoading={marketDataLoading}
          />
        )}

        <Box sx={{ ...bottomCloseButtonStyle }}>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              px: 4,
              backgroundColor: tokens.palette.primary,
              '&:hover': {
                backgroundColor: tokens.palette.primaryA80,
              },
              color: tokens.colors.white,
              borderRadius: "8px", // consistent radius
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BondDetailsModal;
